/**
 * The deck `BuildWaitCards` rotates through while a lesson is being set up.
 *
 * Three kinds, each with a mono eyebrow label:
 * - `tip`: a real feature of the app, worded the way its UI words it (every
 *   one was checked against the component that implements it).
 * - `science`: a well-established finding about how people learn, tied to
 *   what the app does about it where it does something.
 * - `quote`: ONLY quotes with a reliable, documented source - the `source`
 *   field names it. A popular quote with a shaky attribution is left out
 *   rather than mis-credited.
 */

export type WaitCardKind = 'tip' | 'science' | 'quote';

export interface WaitCard {
  id: string;
  kind: WaitCardKind;
  /** The tip, the finding, or the quote itself (without quote marks). */
  body: string;
  /** Quotes only: who said it. */
  author?: string;
  /** Quotes only: where it is documented. */
  source?: string;
}

export const WAIT_CARD_LABEL: Record<WaitCardKind, string> = {
  tip: 'Tip',
  science: 'Study science',
  quote: 'Quote',
};

const TIPS: WaitCard[] = [
  {
    id: 'tip-concept-panel',
    kind: 'tip',
    body: 'Click a concept on the mind map to open it: what to know first, the key points, and the mistakes to watch out for.',
  },
  {
    id: 'tip-tutor-sources',
    kind: 'tip',
    body: "Ask the tutor anything about this lesson. It answers from the lesson's sources and shows where each answer came from.",
  },
  {
    id: 'tip-explain',
    kind: 'tip',
    body: 'Select any passage in the lesson text and choose Explain to send it straight to the tutor.',
  },
  {
    id: 'tip-save-notes',
    kind: 'tip',
    body: 'Select a passage and choose Save to notes to keep it with this lesson.',
  },
  {
    id: 'tip-highlights',
    kind: 'tip',
    body: 'Highlights turns the lesson into a short set of cards, a quick recap before you practise.',
  },
  {
    id: 'tip-generate',
    kind: 'tip',
    body: 'In Practice, choose Generate to make a quiz or flashcards from this lesson.',
  },
  {
    id: 'tip-quick-bytes',
    kind: 'tip',
    body: 'Quick Bytes in Mindspace turns what you have learned into bite-sized cards for a spare few minutes.',
  },
  {
    id: 'tip-practice-arena',
    kind: 'tip',
    body: 'Practice Arena in Mindspace shows the flashcards and quizzes that are due, so reviews come back on time.',
  },
  {
    id: 'tip-focus',
    kind: 'tip',
    body: 'Need to concentrate? The Focus room in Wellbeing runs a timer for a distraction-free study block.',
  },
  {
    id: 'tip-versions',
    kind: 'tip',
    body: 'Want a different angle? Edit the prompt and create a new version. Your earlier versions stay one click away.',
  },
];

const SCIENCE: WaitCard[] = [
  {
    id: 'sci-retrieval',
    kind: 'science',
    body: 'Testing yourself beats rereading. Pulling an idea back out of memory strengthens it, which is why Practice has quizzes and flashcards.',
  },
  {
    id: 'sci-spacing',
    kind: 'science',
    body: 'Spread your reviews out. Coming back after a day, then a few days, keeps more than one long session. Mindspace schedules those reviews for you.',
  },
  {
    id: 'sci-interleaving',
    kind: 'science',
    body: 'Mix related topics when you practise. Switching between kinds of problem teaches you which method fits which one.',
  },
  {
    id: 'sci-self-explain',
    kind: 'science',
    body: 'Explain it in your own words. Where you get stuck is the gap: ask the tutor about exactly that part.',
  },
  {
    id: 'sci-dual-coding',
    kind: 'science',
    body: 'Pair words with pictures. A diagram beside an explanation gives you two ways back to the idea. Your mind map is one.',
  },
  {
    id: 'sci-sleep',
    kind: 'science',
    body: 'Sleep helps new memories settle. A short review today and another tomorrow works better than a late-night cram.',
  },
  {
    id: 'sci-pretesting',
    kind: 'science',
    body: 'Try to answer before you know. A guess made first, even a wrong one, helps the right answer stick when you see it.',
  },
  {
    id: 'sci-examples',
    kind: 'science',
    body: 'Find a concrete example for every abstract idea. Examples make ideas easier to remember and to use.',
  },
];

const QUOTES: WaitCard[] = [
  {
    id: 'quote-einstein-curious',
    kind: 'quote',
    body: 'I have no special talents. I am only passionately curious.',
    author: 'Albert Einstein',
    source: 'Letter to Carl Seelig, 1952',
  },
  {
    id: 'quote-einstein-questioning',
    kind: 'quote',
    body: 'The important thing is not to stop questioning. Curiosity has its own reason for existing.',
    author: 'Albert Einstein',
    source: 'Quoted by William Miller, LIFE magazine, 1955',
  },
  {
    id: 'quote-plutarch',
    kind: 'quote',
    body: 'The mind does not require filling like a bottle, but rather, like wood, it only requires kindling.',
    author: 'Plutarch',
    source: 'On Listening to Lectures, Moralia',
  },
  {
    id: 'quote-feynman-fool',
    kind: 'quote',
    body: 'The first principle is that you must not fool yourself, and you are the easiest person to fool.',
    author: 'Richard Feynman',
    source: 'Caltech commencement address, 1974',
  },
  {
    id: 'quote-feynman-create',
    kind: 'quote',
    body: 'What I cannot create, I do not understand.',
    author: 'Richard Feynman',
    source: 'His Caltech blackboard, 1988',
  },
  {
    id: 'quote-newton',
    kind: 'quote',
    body: 'If I have seen further it is by standing on the shoulders of Giants.',
    author: 'Isaac Newton',
    source: 'Letter to Robert Hooke, 1675',
  },
  {
    id: 'quote-durant',
    kind: 'quote',
    body: 'We are what we repeatedly do. Excellence, then, is not an act, but a habit.',
    author: 'Will Durant',
    source: 'The Story of Philosophy, 1926',
  },
  {
    id: 'quote-seuss',
    kind: 'quote',
    body: 'The more that you read, the more things you will know. The more that you learn, the more places you’ll go.',
    author: 'Dr. Seuss',
    source: 'I Can Read With My Eyes Shut!, 1978',
  },
];

/** Interleaved tip → science → quote, so neighbours are never the same kind
 *  - including across the wrap from the last card back to the first. Cards
 *  left over once the shortest list runs out (extra tips) are slotted in after
 *  a science card, between two other kinds. */
function interleave(
  primary: WaitCard[],
  middle: WaitCard[],
  last: WaitCard[]
): WaitCard[] {
  const rounds = Math.min(primary.length, middle.length, last.length);
  const extras = [
    ...primary.slice(rounds),
    ...middle.slice(rounds),
    ...last.slice(rounds),
  ];
  const out: WaitCard[] = [];
  const every = extras.length
    ? Math.max(1, Math.floor(rounds / extras.length))
    : 0;
  for (let i = 0; i < rounds; i += 1) {
    out.push(primary[i], middle[i]);
    if (extras.length && i % every === every - 1) {
      const extra = extras.shift();
      if (
        extra &&
        extra.kind !== middle[i].kind &&
        extra.kind !== last[i].kind
      ) {
        out.push(extra);
      } else if (extra) {
        extras.unshift(extra);
      }
    }
    out.push(last[i]);
  }
  return out;
}

export const WAIT_CARDS: readonly WaitCard[] = interleave(
  TIPS,
  SCIENCE,
  QUOTES
);

/** A stable start index for a lesson: the same lesson always opens on the
 *  same card across re-renders, different lessons open on different ones. */
export function firstCardIndex(seed: string | undefined, count: number) {
  if (!seed || count <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash % count;
}
